import React from "react";
import { AlertTriangle, ExternalLink, ShieldAlert, ShieldCheck } from "lucide-react";

const SEVERITY_STYLES = {
  CRITICAL: {
    badge: "bg-red-700 text-white",
    border: "border-red-700",
    label: "Critical",
  },
  HIGH: {
    badge: "bg-amber-600 text-white",
    border: "border-amber-600",
    label: "High",
  },
  MEDIUM: {
    badge: "bg-yellow-500 text-black",
    border: "border-yellow-500",
    label: "Medium",
  },
};

function formatAmountStatus(s) {
  if (!s) return "";
  return s
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Hidden Cost / Future Fee Watch card.
 * Renders only when the backend says render_card=true.
 * Shows a blunt, opposition-research-style breakdown of buried mechanisms.
 */
export default function HiddenCostCard({ findings, renderCard, emptyState }) {
  if (!renderCard) {
    return (
      <section
        className="paper-card rounded-sm p-4 sm:p-5 mb-5 border-l-4 border-emerald-600"
        data-testid="hidden-cost-empty"
      >
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
          <div>
            <div className="section-label mb-1">Hidden Cost / Future Fee Watch</div>
            <div className="text-sm text-[hsl(var(--ink-2))] leading-relaxed">
              {emptyState ||
                "No non-trivial hidden cost or future-fee mechanism was detected in the scanned text."}
            </div>
            <div className="text-[11px] text-[hsl(var(--ink-muted))] mt-2">
              The audit scans every bill for fees, taxes, contributions, funds,
              future guidance, agency authority, exemptions, penalties, and
              other buried mechanisms a normal voter would care about. A clean
              result is not a guarantee — read the citations to verify.
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="paper-card-elevated rounded-sm p-5 mb-5 border-l-4 border-red-700"
      data-testid="hidden-cost-card"
    >
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-dashed border-[hsl(var(--rule))]">
        <ShieldAlert className="w-5 h-5 text-red-700" />
        <div className="section-label">Hidden Cost / Future Fee Watch</div>
        <span className="ml-auto text-[11px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))]">
          {findings?.length || 0} finding{(findings?.length || 0) === 1 ? "" : "s"}
        </span>
      </div>

      <p className="text-xs text-[hsl(var(--ink-muted))] leading-relaxed mb-4 max-w-3xl">
        These are mechanisms in the bill text that create or expand fees, funds,
        contributions, agency authority, exemptions, penalties, or future
        rule-making — items a hostile contract reviewer would flag. Each is
        anchored to a specific section and code reference. Use the citation to
        verify the exact bill language.
      </p>

      <div className="space-y-4">
        {(findings || []).map((f, i) => {
          const sev = (f.severity || "MEDIUM").toUpperCase();
          const style = SEVERITY_STYLES[sev] || SEVERITY_STYLES.MEDIUM;
          const cite = f.citation || {};
          return (
            <article
              key={i}
              className={`bg-white border-l-4 ${style.border} border-y border-r border-[hsl(var(--rule))] rounded-sm p-4`}
              data-testid={`hidden-cost-finding-${i}`}
            >
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span
                  className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm ${style.badge}`}
                  data-testid={`finding-severity-${i}`}
                >
                  {style.label}
                </span>
                {f.creates_new_pathway && f.creates_new_pathway !== "none" && (
                  <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm bg-[hsl(var(--ink))] text-[hsl(var(--paper))]">
                    {f.creates_new_pathway.replace(/_/g, " ")}
                  </span>
                )}
                {f.future_guidance_dependent && (
                  <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border border-amber-600 text-amber-800">
                    Future guidance
                  </span>
                )}
                {f.local_agency_authority_preserved && (
                  <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border border-blue-700 text-blue-800">
                    Local authority preserved
                  </span>
                )}
              </div>

              <h4 className="font-serif text-base text-[hsl(var(--ink))] leading-snug mb-1">
                {f.issue || "Hidden mechanism"}
              </h4>

              {f.what_it_appears_to_do && (
                <p className="text-sm text-[hsl(var(--ink-2))] leading-relaxed mb-2">
                  {f.what_it_appears_to_do}
                </p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-[hsl(var(--ink-2))] mb-2">
                {f.who_gains_authority && (
                  <div>
                    <span className="font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))]">
                      Who gains authority:
                    </span>{" "}
                    {f.who_gains_authority}
                  </div>
                )}
                {f.who_may_pay && (
                  <div>
                    <span className="font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))]">
                      Who may pay:
                    </span>{" "}
                    {f.who_may_pay}
                  </div>
                )}
                {f.amount_status && (
                  <div>
                    <span className="font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))]">
                      Amount:
                    </span>{" "}
                    {formatAmountStatus(f.amount_status)}
                  </div>
                )}
              </div>

              {f.why_it_matters && (
                <div className="text-sm text-[hsl(var(--ink))] leading-relaxed bg-[hsl(var(--paper-2))] border-l-2 border-[hsl(var(--rule))] pl-3 py-2 my-2">
                  <span className="font-mono uppercase tracking-wider text-[10px] text-[hsl(var(--ink-muted))] block mb-1">
                    Why it matters
                  </span>
                  {f.why_it_matters}
                </div>
              )}

              {f.plain_english_takeaway && (
                <div className="text-xs italic text-[hsl(var(--ink-2))] mt-2">
                  Plain-English takeaway: {f.plain_english_takeaway}
                </div>
              )}

              {f.anchor_quote && (
                <blockquote className="quote-block mt-3 text-sm">
                  &ldquo;{f.anchor_quote}&rdquo;
                </blockquote>
              )}

              {/* Citation footer */}
              <div className="mt-3 pt-2 border-t border-dashed border-[hsl(var(--rule))] flex items-center gap-2 flex-wrap text-[11px] font-mono text-[hsl(var(--ink-muted))]">
                {cite.section_label && (
                  <span data-testid={`finding-section-${i}`}>{cite.section_label}</span>
                )}
                {cite.code_section && (
                  <>
                    <span>·</span>
                    <span data-testid={`finding-code-${i}`}>{cite.code_section}</span>
                  </>
                )}
                {cite.page && (
                  <>
                    <span>·</span>
                    <span data-testid={`finding-page-${i}`}>p. {cite.page}</span>
                  </>
                )}
                {cite.pdf && !cite.page && (
                  <>
                    <span>·</span>
                    <span>PDF (page not detected)</span>
                  </>
                )}
                {cite.source_url && !cite.source_url.startsWith("manual://") && !cite.source_url.startsWith("scan://") && (
                  <a
                    href={cite.source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-auto inline-flex items-center gap-1 text-[hsl(var(--accent-blue))] hover:underline normal-case tracking-normal"
                    data-testid={`finding-source-link-${i}`}
                  >
                    Open official source <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {!cite.source_url && (
                  <span className="ml-auto inline-flex items-center gap-1 text-amber-700">
                    <AlertTriangle className="w-3 h-3" /> Source link unavailable for this finding
                  </span>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
